/*
 * XYNE Redesign — Predictive search
 *
 * Opens via:
 *   - Click any [data-rd-search-open] or Atlas Bolt's existing search trigger
 *     (.header__icon--search, [aria-controls*="search"], etc.)
 *   - Cmd+K / Ctrl+K keyboard shortcut
 *   - Slash "/" key when not focused on an input (power-user nav)
 *
 * Fetches /search/suggest.json with debounced 200ms input. Renders product
 * cards, articles, pages, and collection results in grouped sections.
 *
 * Keyboard: ↑↓ navigate results, Enter follows the active link, Esc closes.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var MIN_QUERY_LEN = 2;

  function init() {
    var overlay = document.querySelector('[data-rd-search-overlay]');
    if (!overlay) return;

    var input = overlay.querySelector('[data-rd-search-input]');
    var clearBtn = overlay.querySelector('[data-rd-search-clear]');
    var idleEl = overlay.querySelector('[data-rd-search-idle]');
    var loadingEl = overlay.querySelector('[data-rd-search-loading]');
    var resultsEl = overlay.querySelector('[data-rd-search-results]');
    var emptyEl = overlay.querySelector('[data-rd-search-empty]');
    var emptyQuery = overlay.querySelector('[data-rd-search-empty-query]');
    var form = overlay.querySelector('[data-rd-search-form]');

    var debounceTimer = null;
    var lastFocus = null;
    var lastQuery = '';
    var resultCache = {};

    // ---------- Open / Close ----------
    function open() {
      if (!overlay.hidden && document.body.classList.contains('rd-search-open')) return;
      lastFocus = document.activeElement;
      overlay.hidden = false;
      requestAnimationFrame(function () {
        document.body.classList.add('rd-search-open');
        // Focus the input after the overlay paints
        setTimeout(function () { if (input) input.focus(); }, 60);
      });
    }
    function close() {
      document.body.classList.remove('rd-search-open');
      setTimeout(function () {
        if (!document.body.classList.contains('rd-search-open')) {
          overlay.hidden = true;
        }
      }, 220);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    }

    // Wire close
    overlay.querySelectorAll('[data-rd-search-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    // Backdrop click closes (overlay itself, not panel)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });

    // ---------- Trigger interception ----------
    // Custom triggers — anything with [data-rd-search-open] explicitly opted in
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-rd-search-open]');
      if (trigger) {
        e.preventDefault();
        open();
        return;
      }
      // Atlas Bolt's stock search triggers — only intercept on rd-redesigned pages
      if (!document.body.classList.contains('rd-redesigned')) return;
      var stockTrigger = e.target.closest('[aria-controls*="search-modal"], .header__icon--search, [data-modal-toggle*="search"], details-modal[name*="search"] summary');
      if (stockTrigger) {
        e.preventDefault();
        e.stopPropagation();
        open();
      }
    }, true);

    // ---------- Keyboard shortcuts ----------
    document.addEventListener('keydown', function (e) {
      // Esc closes
      if (e.key === 'Escape' && document.body.classList.contains('rd-search-open')) {
        close();
        return;
      }
      // Cmd+K / Ctrl+K opens
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        open();
        return;
      }
      // "/" opens when not focused on an input/textarea
      if (e.key === '/' && !overlay.hidden === false) {
        var tag = (document.activeElement && document.activeElement.tagName) || '';
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !document.activeElement.isContentEditable) {
          e.preventDefault();
          open();
        }
      }
    });

    // ---------- Input handling ----------
    function setState(state) {
      // state: 'idle' | 'loading' | 'results' | 'empty'
      // Null-safe — `loadingEl` is intentionally absent in markup
      // (skeleton bars removed 2026-05-04). Other states could
      // also be removed in the future without breaking the script.
      if (idleEl)    idleEl.hidden    = state !== 'idle';
      if (loadingEl) loadingEl.hidden = state !== 'loading';
      if (resultsEl) resultsEl.hidden = state !== 'results';
      if (emptyEl)   emptyEl.hidden   = state !== 'empty';
    }

    function handleInput(value) {
      lastQuery = value.trim();
      clearBtn.hidden = !lastQuery;

      if (debounceTimer) clearTimeout(debounceTimer);

      if (lastQuery.length === 0) {
        setState('idle');
        return;
      }
      if (lastQuery.length < MIN_QUERY_LEN) {
        setState('idle');
        return;
      }

      // Cache hit?
      if (resultCache[lastQuery]) {
        renderResults(resultCache[lastQuery], lastQuery);
        return;
      }

      // Debounced fetch
      setState('loading');
      debounceTimer = setTimeout(function () {
        fetchSuggestions(lastQuery);
      }, DEBOUNCE_MS);
    }

    if (input) {
      input.addEventListener('input', function () { handleInput(input.value); });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        handleInput('');
        input.focus();
      });
    }

    // Chip suggestions in the idle state
    overlay.querySelectorAll('[data-rd-search-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        input.value = chip.dataset.rdSearchChip;
        handleInput(input.value);
        input.focus();
      });
    });

    // ---------- Smart navigation map ----------
    // Comprehensive keyword → destination map. Designed so any word
    // that describes something on the Xyne site surfaces a relevant
    // link, even when Shopify's text search has nothing to return.
    // Categories: site sections, top-level + sub categories, every
    // product family by concept (energy / focus / gut / etc.), every
    // major ingredient, every wellness term, every policy. Add here
    // when new content ships. (#64 — expanded 2026-05-04)
    var smartNav = [
      // ── Site sections ──────────────────────────────────────────
      { keywords: ['blog', 'article', 'news', 'journal', 'post', 'reading'], title: 'Journal',
        meta: 'Articles + ingredient deep-dives', kind: 'article', url: '/blogs/news' },
      { keywords: ['quiz', 'find your', 'recommend', 'match', 'help me choose', 'which one'], title: 'Take the quiz',
        meta: 'Match your formula in 60 seconds', kind: 'page', url: '/pages/quiz' },
      { keywords: ['ingredient', 'science', 'research', 'study', 'pubmed', 'evidence', 'citation', 'active'], title: 'Ingredient library',
        meta: 'Every active, dose, and citation', kind: 'page', url: '/pages/ingredients' },
      { keywords: ['faq', 'help', 'question', 'how do', 'how does', 'what is', 'what are'], title: 'FAQ',
        meta: 'Common questions + sublingual science', kind: 'page', url: '/pages/faqs' },
      { keywords: ['contact', 'support', 'reach', 'email', 'inquiry', 'message'], title: 'Contact',
        meta: 'Get in touch with our team', kind: 'page', url: '/pages/contact' },
      { keywords: ['track', 'order status', 'shipping update', 'where is my', 'delivery'], title: 'Track your order',
        meta: 'Check shipping status', kind: 'page', url: '/pages/track-order' },
      { keywords: ['about', 'who are', 'who is xyne', 'story', 'mission', 'founder'], title: 'About Xyne',
        meta: 'Our story + the founders', kind: 'page', url: '/pages/about-us' },
      { keywords: ['shop', 'browse', 'catalog', 'catalogue', 'all products', 'everything'], title: 'Shop the catalog',
        meta: 'Every formula in one place', kind: 'collection', url: '/pages/strips' },
      { keywords: ['how it work', 'sublingual', 'sub-lingual', 'absorption', 'tongue', '30 second', 'fast'], title: 'How sublingual works',
        meta: 'Why strips beat pills', kind: 'page', url: '/pages/how-sublingual-works' },

      // ── Brand comparisons (added 2026-06-06) ──────────────────
      // Surface the head-to-head pages first when someone types a
      // competitor name. Shopify's search returns them too, but the
      // dynamic Pages section renders below products and articles —
      // these nav matches push them to the top of the overlay.
      { keywords: ['ag1', 'athletic greens', 'ag-1', 'a.g.1', 'a g 1'], title: 'Xyne vs. AG1',
        meta: 'Sublingual strips vs powdered greens drink', kind: 'page', url: '/pages/xyne-vs-ag1' },
      { keywords: ['bloom', 'bloom nutrition', 'bloomnu'], title: 'Xyne vs. Bloom',
        meta: 'Sublingual strips vs Bloom powders', kind: 'page', url: '/pages/xyne-vs-bloom' },
      { keywords: ['cymbiotika', 'cymbiotica', 'cymbio'], title: 'Xyne vs. Cymbiotika',
        meta: 'Sublingual strips vs liquid sublingual pouches', kind: 'page', url: '/pages/xyne-vs-cymbiotika' },
      { keywords: ['compare', 'comparison', 'versus', ' vs ', 'vs.'], title: 'Compare Xyne',
        meta: 'All brand + format comparisons', kind: 'page', url: '/pages/compare' },

      // ── Top-level categories (Categories page tiles) ───────────
      { keywords: ['categor', 'lines', 'two categor'], title: 'Browse categories',
        meta: 'Supplements + Skin Care', kind: 'collection', url: '/pages/shop' },
      { keywords: ['skin', 'skincare', 'face', 'topical', 'serum', 'cream', 'glow', 'complexion'], title: 'Skin Care (coming soon)',
        meta: 'Inside-out skin wellness — on the way', kind: 'collection', url: '/pages/shop' },

      // ── Supplement sub-formats ─────────────────────────────────
      { keywords: ['supplement', 'vitamin'], title: 'Supplements',
        meta: 'Strips today + formats coming soon', kind: 'page', url: '/pages/supplements' },
      { keywords: ['strip', 'oral strip', 'dissolvable', 'sublingual strip', 'film'], title: 'Strips',
        meta: 'Eight clinically-dosed formulas', kind: 'collection', url: '/pages/strips' },
      { keywords: ['powder', 'sachet', 'mix', 'shake'], title: 'Powders (coming soon)',
        meta: 'Single-serve actives — formulating', kind: 'page', url: '/pages/supplements' },
      { keywords: ['capsule', 'pill', 'tablet'], title: 'Capsules (coming soon)',
        meta: 'Vegetable-cellulose capsules — formulating', kind: 'page', url: '/pages/supplements' },
      { keywords: ['liquid', 'tincture', 'drop', 'oil'], title: 'Liquids (coming soon)',
        meta: 'Tincture-style sublingual — formulating', kind: 'page', url: '/pages/supplements' },

      // ── Product concepts (matches across the 8 strip formulas) ─
      { keywords: ['energy', 'tired', 'fatigue', 'wake up', 'caffeine', 'morning'], title: 'Energy Strips',
        meta: 'Caffeine + B12 + L-theanine', kind: 'product', url: '/products/energy-strips' },
      { keywords: ['hangover', 'recovery', 'after drinking', 'too much', 'morning after'], title: 'Hangover Strips',
        meta: 'DHM + curcumin + electrolytes', kind: 'product', url: '/products/hangover-strips' },
      { keywords: ['probiotic', 'gut', 'digestion', 'metabolism', 'bloat', 'digestive'], title: 'Probiotic + Metabolism Strips',
        meta: 'Bifidobacterium + actives', kind: 'product', url: '/products/probiotic-metabolism-strips' },
      { keywords: ['appetite', 'weight', 'hunger', 'cravings', 'satiety', 'glp'], title: 'Appetite Balance & Weight Support',
        meta: 'Saffron + chromium', kind: 'product', url: '/products/appetite-balance-strips' },
      { keywords: ['bone', 'calcium', 'osteo', 'd3', 'k2', 'vitamin d'], title: 'Bone Support Strips',
        meta: 'D3 + K2 + magnesium', kind: 'product', url: '/products/bone-support-strips' },
      { keywords: ['focus', 'cognitive', 'calm', 'gaba', 'relax', 'anxiety', 'stress', 'mental'], title: 'Cognitive Relax Strips',
        meta: 'GABA + L-theanine', kind: 'product', url: '/products/cognitive-relax-strips' },
      { keywords: ['iron', 'anemia', 'stamina', 'tired blood', 'low iron'], title: 'Iron Strips',
        meta: 'Ferric saccharate — gentle iron', kind: 'product', url: '/products/iron-strips' },
      { keywords: ['mushroom', "lion's mane", 'lions mane', 'lion mane', 'reishi', 'cordyceps', 'adaptogen'], title: 'Mushroom Focus Strips',
        meta: "Lion's Mane + adaptogenic mushrooms", kind: 'product', url: '/products/mushroom-focus-strips' },
      { keywords: ['beauty', 'collagen', 'skin', 'hair', 'nails', 'glow', 'anti-aging', 'aging', 'wrinkle', 'vitamin e'], title: 'Beauty + Collagen Strips',
        meta: 'Collagen peptide 100mg + Vitamin E', kind: 'product', url: '/products/beauty-collagen-strips' },
      { keywords: ['sleep', 'melatonin', 'insomnia', 'bedtime', 'rest', 'valerian', 'chamomile', 'lavender', 'wind down', 'nighttime'], title: 'Sleep Strips',
        meta: 'Melatonin 1mg + botanicals', kind: 'product', url: '/products/sleep-strips' },
      { keywords: ['libido', 'intimate', 'vitality', 'drive', 'desire', 'passion', 'romance', 'sex', 'cordyceps', 'shilajit', 'oyster', 'zinc'], title: 'Intimate Vitality Strips',
        meta: 'Cordyceps + Shilajit + Oyster Peptide', kind: 'product', url: '/products/libido-support-strips' },

      // ── Wellness / lifestyle terms ─────────────────────────────
      { keywords: ['vegan', 'plant', 'plant-based', 'plant based'], title: 'Plant-based formulas',
        meta: 'Pullulan strip base, no gelatin', kind: 'page', url: '/pages/ingredients' },
      { keywords: ['gluten', 'gluten-free', 'gluten free', 'celiac'], title: 'Gluten-free',
        meta: 'Every formula is gluten-free', kind: 'page', url: '/pages/ingredients' },
      { keywords: ['non-gmo', 'non gmo', 'gmo'], title: 'Non-GMO',
        meta: 'Every formula non-GMO', kind: 'page', url: '/pages/ingredients' },
      { keywords: ['halal', 'kosher'], title: 'Halal-friendly',
        meta: 'Plant-based, halal-compatible', kind: 'page', url: '/pages/ingredients' },
      { keywords: ['pullulan', 'film base', 'strip material'], title: 'Pullulan strip base',
        meta: 'Plant-derived dissolvable film', kind: 'page', url: '/pages/ingredients' },

      // ── Subscription / commerce ────────────────────────────────
      { keywords: ['subscribe', 'subscription', 'cancel', 'auto-ship', 'auto ship', 'recurring', 'save'], title: 'Subscribe & save',
        meta: 'Skip, swap, cancel anytime', kind: 'page', url: '/pages/subscription-policy' },
      { keywords: ['cart', 'bag', 'checkout', 'order'], title: 'Cart',
        meta: 'Review your order', kind: 'page', url: '/cart' },
      { keywords: ['account', 'sign in', 'login', 'log in', 'my account'], title: 'Sign in',
        meta: 'Access your account', kind: 'page', url: '/account/login' },
      { keywords: ['register', 'create account', 'sign up', 'new account'], title: 'Create account',
        meta: 'Join Xyne', kind: 'page', url: '/account/register' },

      // ── Policies + legal ───────────────────────────────────────
      { keywords: ['privacy', 'data', 'cookie', 'gdpr', 'ccpa'], title: 'Privacy policy',
        meta: 'How we handle your data', kind: 'page', url: '/pages/privacy-policy' },
      { keywords: ['return', 'refund', 'money back', '30 day', 'send back'], title: 'Refund policy',
        meta: '30-day return window', kind: 'page', url: '/pages/refund-policy' },
      { keywords: ['terms', 'service', 'agreement', 'legal'], title: 'Terms of service',
        meta: 'Site terms + conditions', kind: 'page', url: '/pages/terms-of-service' },
      { keywords: ['shipping', 'delivery', 'free shipping', 'no minimum'], title: 'Shipping info',
        meta: 'Free shipping on every order', kind: 'page', url: '/pages/faqs' },
      { keywords: ['do not sell', 'opt out', 'data sharing', 'data choice'], title: 'Privacy choices',
        meta: 'Control how your data is shared', kind: 'page', url: '/pages/data-sharing-opt-out' },
    ];

    function getSmartNavMatches(query) {
      var q = query.toLowerCase().trim();
      if (q.length < 2) return [];
      return smartNav.filter(function (entry) {
        return entry.keywords.some(function (kw) { return q.indexOf(kw) !== -1 || kw.indexOf(q) !== -1; });
      }).slice(0, 4);  // cap at 4 nav suggestions to avoid overwhelming
    }

    // ---------- Fetch ----------
    function fetchSuggestions(query) {
      // Bumped limit 6→10 + added `tag` to searchable fields so e.g.
      // a product tagged "energy" surfaces when user types "energy".
      var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=product,article,page,collection' +
                '&resources[limit]=10' +
                '&resources[options][unavailable_products]=last' +
                '&resources[options][fields]=title,vendor,product_type,body,tag';
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('Search failed');
          return r.json();
        })
        .then(function (data) {
          if (lastQuery !== query) return;  // user kept typing; ignore stale result
          resultCache[query] = data;
          renderResults(data, query);
        })
        .catch(function () {
          if (lastQuery !== query) return;
          renderEmpty(query);
        });
    }

    // ---------- Render ----------
    function renderResults(data, query) {
      var resources = (data && data.resources && data.resources.results) || {};
      var products = resources.products || [];
      var articles = resources.articles || [];
      var pages = resources.pages || [];
      var collections = resources.collections || [];
      var navMatches = getSmartNavMatches(query);

      // De-duplicate: drop nav entries whose URL already appears in
      // Shopify's results (so we don't show "FAQ" nav-link AND "FAQ"
      // page-result side by side).
      var shopifyUrls = {};
      [].concat(products, articles, pages, collections).forEach(function (item) {
        if (item && item.url) shopifyUrls[item.url] = true;
      });
      navMatches = navMatches.filter(function (n) { return !shopifyUrls[n.url]; });

      var totalCount = products.length + articles.length + pages.length + collections.length + navMatches.length;
      if (totalCount === 0) {
        renderEmpty(query);
        return;
      }

      var html = '';

      // Smart nav matches first — surface direct site links that
      // Shopify's text search alone wouldn't have found. (#64)
      if (navMatches.length) {
        html += '<div class="rd-search__result-group" data-rd-search-group>' +
                  '<span class="rd-search__result-group-label">Quick links</span>' +
                  navMatches.map(navHtml).join('') +
                '</div>';
      }

      if (products.length) {
        html += '<div class="rd-search__result-group" data-rd-search-group>' +
                  '<span class="rd-search__result-group-label">Products</span>' +
                  products.slice(0, 4).map(productHtml).join('') +
                '</div>';
      }
      // Pages render BEFORE articles + collections (re-ordered 2026-06-06).
      // Brand comparison pages and other branded pages are usually higher-
      // intent results than blog articles or collection listings.
      if (pages.length) {
        html += '<div class="rd-search__result-group" data-rd-search-group>' +
                  '<span class="rd-search__result-group-label">Pages</span>' +
                  pages.slice(0, 4).map(function (p) { return lineHtml(p, 'page'); }).join('') +
                '</div>';
      }
      if (articles.length) {
        html += '<div class="rd-search__result-group" data-rd-search-group>' +
                  '<span class="rd-search__result-group-label">Articles</span>' +
                  articles.slice(0, 3).map(function (a) { return lineHtml(a, 'article'); }).join('') +
                '</div>';
      }
      if (collections.length) {
        html += '<div class="rd-search__result-group" data-rd-search-group>' +
                  '<span class="rd-search__result-group-label">Collections</span>' +
                  collections.slice(0, 3).map(function (c) { return lineHtml(c, 'collection'); }).join('') +
                '</div>';
      }

      // "View all results" footer link
      html += '<a class="rd-search__view-all" href="/search?q=' + encodeURIComponent(query) + '">' +
                '<span>View all results for "<strong>' + escapeHtml(query) + '</strong>"</span>' +
                '<svg viewBox="0 0 24 24" width="14" height="14" fill="none">' +
                  '<path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</a>';

      resultsEl.innerHTML = html;
      setState('results');

      // Reset keyboard focus
      activeIndex = -1;
    }

    function productHtml(p) {
      var imgUrl = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
      var price = formatMoneyFromPrice(p.price);
      var was = '';
      if (p.compare_at_price_max && p.compare_at_price_max > p.price_max) {
        was = '<span class="rd-search__result-price-was">' + formatMoneyFromPrice(p.compare_at_price_max) + '</span>';
      }
      var meta = '';
      if (p.product_type) meta = escapeHtml(p.product_type);
      else if (p.vendor) meta = escapeHtml(p.vendor);

      return '<a class="rd-search__result-product" href="' + escapeAttr(p.url) + '" data-rd-search-result>' +
        '<span class="rd-search__result-img">' +
          (imgUrl ? '<img src="' + escapeAttr(imgUrl) + '" alt="" loading="lazy" width="56" height="56">' : '') +
        '</span>' +
        '<span class="rd-search__result-body">' +
          '<span class="rd-search__result-title">' + escapeHtml(p.title) + '</span>' +
          (meta ? '<span class="rd-search__result-meta">' + meta + '</span>' : '') +
        '</span>' +
        '<span class="rd-search__result-price">' + was + price + '</span>' +
      '</a>';
    }

    function lineHtml(item, kind) {
      var icon;
      if (kind === 'article') {
        icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      } else if (kind === 'collection') {
        icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M3 8a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
      } else {
        icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none"><path d="M6 3h9l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M14 3v6h6" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
      }

      return '<a class="rd-search__result-line" href="' + escapeAttr(item.url) + '" data-rd-search-result>' +
        '<span class="rd-search__result-line-icon">' + icon + '</span>' +
        '<span class="rd-search__result-line-body">' +
          '<span class="rd-search__result-line-title">' + escapeHtml(item.title) + '</span>' +
          (item.vendor ? '<span class="rd-search__result-line-meta">' + escapeHtml(item.vendor) + '</span>' : '') +
        '</span>' +
      '</a>';
    }

    // Smart-nav entries — share the same layout as article/page line rows
    // so the "Quick links" group reads consistently with Articles/Pages
    // groups below it. (#64)
    function navHtml(entry) {
      var icon = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none">' +
                   '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6"/>' +
                   '<path d="M3 12h18M12 3a13 13 0 010 18M12 3a13 13 0 000 18" stroke="currentColor" stroke-width="1.4"/>' +
                 '</svg>';
      return '<a class="rd-search__result-line" href="' + escapeAttr(entry.url) + '" data-rd-search-result>' +
        '<span class="rd-search__result-line-icon">' + icon + '</span>' +
        '<span class="rd-search__result-line-body">' +
          '<span class="rd-search__result-line-title">' + escapeHtml(entry.title) + '</span>' +
          '<span class="rd-search__result-line-meta">' + escapeHtml(entry.meta) + '</span>' +
        '</span>' +
      '</a>';
    }

    function renderEmpty(query) {
      // Even when Shopify returns nothing, surface smart-nav matches
      // before falling through to the empty-state card. Helps queries
      // like "blog" / "article" / "policy" that don't appear in any
      // product/article body but DO map to a known site section. (#64)
      var navMatches = getSmartNavMatches(query);
      if (navMatches.length) {
        var html = '<div class="rd-search__result-group" data-rd-search-group>' +
                     '<span class="rd-search__result-group-label">Quick links</span>' +
                     navMatches.map(navHtml).join('') +
                   '</div>' +
                   '<a class="rd-search__view-all" href="/search?q=' + encodeURIComponent(query) + '">' +
                     '<span>View all results for "<strong>' + escapeHtml(query) + '</strong>"</span>' +
                     '<svg viewBox="0 0 24 24" width="14" height="14" fill="none">' +
                       '<path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
                     '</svg>' +
                   '</a>';
        resultsEl.innerHTML = html;
        setState('results');
        activeIndex = -1;
        return;
      }
      if (emptyQuery) emptyQuery.textContent = query;
      setState('empty');
    }

    // ---------- Keyboard nav within results ----------
    var activeIndex = -1;
    function getResultLinks() {
      return Array.prototype.slice.call(resultsEl.querySelectorAll('[data-rd-search-result]'));
    }
    overlay.addEventListener('keydown', function (e) {
      if (overlay.hidden) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;

      var links = getResultLinks();
      if (links.length === 0) {
        if (e.key === 'Enter' && lastQuery) {
          // Submit the form to the search results page
          form.submit();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = (activeIndex + 1) % links.length;
        updateActive(links);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = (activeIndex - 1 + links.length) % links.length;
        updateActive(links);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && links[activeIndex]) {
          e.preventDefault();
          window.location.href = links[activeIndex].href;
        }
      }
    });

    function updateActive(links) {
      links.forEach(function (l, i) {
        if (i === activeIndex) {
          l.setAttribute('data-rd-active', 'true');
          l.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          l.removeAttribute('data-rd-active');
        }
      });
    }

    // ---------- Helpers ----------
    function escapeHtml(s) {
      if (s == null) return '';
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }
    function escapeAttr(s) { return escapeHtml(s); }

    function formatMoneyFromPrice(value) {
      // Shopify suggest.json returns strings like "$30.00" already formatted.
      // If we get a number (cents), format ourselves.
      if (typeof value === 'string' && value) return value;
      if (typeof value === 'number') {
        try {
          var shop = (window.Shopify && window.Shopify.currency) || {};
          var f = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: shop.active || 'USD'
          });
          return f.format(value / 100);
        } catch (e) {
          return '$' + (value / 100).toFixed(2);
        }
      }
      return '';
    }

    // Public API for other features that want to open programmatically
    window.RDSearch = {
      open: open,
      close: close
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
